"use client";

import {
  useTeamsContext,
  useTournamentContext,
} from "@/contexts/TournamentContext";
import { TeamStatus } from "@/types";
import { Icon } from "@iconify/react";
import { differenceInDays, formatDistanceToNow } from "date-fns";
import { enUS, fi } from "date-fns/locale";
import { formatInTimeZone } from "date-fns-tz";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import { useParams, useRouter } from "next/navigation";
import { useMemo } from "react";

function getInitials(name?: string): string {
  if (!name) return "?";
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("") || "?"
  );
}

interface TeamListViewProps {
  showJoinCard?: boolean;
  onJoinTeamClick?: () => void;
  onFindTeammatesClick?: () => void;
}

export function TeamListView({
  showJoinCard,
  onJoinTeamClick,
  onFindTeammatesClick,
}: TeamListViewProps) {
  const t = useTranslations();
  const locale = useLocale();
  const dateLocale = locale === "fi" ? fi : enUS;
  const router = useRouter();
  const params = useParams();
  const { allTeams, currentUserTeam } = useTeamsContext();
  const { players, tournament } = useTournamentContext();

  // Latest joined team on top
  const teams = useMemo(
    () =>
      [...allTeams].sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bTime - aTime;
      }),
    [allTeams],
  );

  const hasJoinCards =
    showJoinCard && (onJoinTeamClick || onFindTeammatesClick);

  const renderJoinCards = (keyPrefix: string) =>
    hasJoinCards ? (
      <>
        {onJoinTeamClick && (
          <button
            key={`${keyPrefix}-create`}
            type="button"
            onClick={onJoinTeamClick}
            className="fc fc-cta-create fc-clickable"
          >
            <svg className="fc-corner tl">
              <use href="#fc-cn" />
            </svg>
            <svg className="fc-corner tr">
              <use href="#fc-cn" />
            </svg>
            <svg className="fc-corner bl">
              <use href="#fc-cn" />
            </svg>
            <svg className="fc-corner br">
              <use href="#fc-cn" />
            </svg>
            <div className="fc-banner">
              {t("tournaments.join.haveTeamButton")}
            </div>
            <div className="fc-cta-icon">
              <Icon
                icon="solar:users-group-rounded-bold"
                className="h-7 w-7"
              />
            </div>
            <div className="fc-cta-text">
              {t("tournaments.join.haveTeamSubtitle")}
            </div>
            <div className="fc-cta-action" aria-hidden="true">
              <Icon icon="solar:alt-arrow-right-bold" />
            </div>
          </button>
        )}
        {onFindTeammatesClick && (
          <button
            key={`${keyPrefix}-search`}
            type="button"
            onClick={onFindTeammatesClick}
            className="fc fc-cta-search fc-clickable"
          >
            <svg className="fc-corner tl">
              <use href="#fc-cn" />
            </svg>
            <svg className="fc-corner tr">
              <use href="#fc-cn" />
            </svg>
            <svg className="fc-corner bl">
              <use href="#fc-cn" />
            </svg>
            <svg className="fc-corner br">
              <use href="#fc-cn" />
            </svg>
            <div className="fc-banner">
              {t("tournaments.join.lookingButton")}
            </div>
            <div className="fc-cta-icon">
              <Icon icon="solar:hand-shake-bold" className="h-7 w-7" />
            </div>
            <div className="fc-cta-text">
              {t("tournaments.join.lookingSubtitle")}
            </div>
            <div className="fc-cta-action" aria-hidden="true">
              <Icon icon="solar:alt-arrow-right-bold" />
            </div>
          </button>
        )}
      </>
    ) : null;

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* Section header */}
      <div className="flex items-center justify-center gap-3">
        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />
        <div className="flex items-center gap-2 px-4">
          <Icon
            icon="solar:users-group-rounded-bold"
            className="h-5 w-5 text-amber-400"
          />
          <h2 className="font-cinzel text-lg font-semibold uppercase tracking-wider text-white/90">
            {t("tournaments.tower.joinedTeams", { count: teams.length })}
          </h2>
        </div>
        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />
      </div>

      {teams.length === 0 && !showJoinCard ? (
        <div className="relative overflow-hidden rounded-2xl border border-default-200/30 bg-gradient-to-br from-default-100/20 to-default-200/10 p-8">
          <div className="flex flex-col items-center text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-default-200/50">
              <Icon
                icon="solar:users-group-rounded-line-duotone"
                className="h-8 w-8 text-default-400"
              />
            </div>
            <p className="text-default-500 max-w-sm">
              {t("tournaments.tower.noTeamsYet")}
            </p>
          </div>
        </div>
      ) : (
        <div className="fc-grid">
          {/* SVG symbol library — referenced by <use> in cards below */}
          <svg
            width="0"
            height="0"
            style={{ position: "absolute" }}
            aria-hidden="true"
          >
            <defs>
              <symbol id="fc-cn" viewBox="0 0 16 16">
                <path
                  d="M2 2 L8 2 M2 2 L2 8 M5 2 Q5 5 8 5 Q8 8 5 8"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.1"
                  strokeLinecap="round"
                />
                <circle cx="2.5" cy="2.5" r="0.9" fill="currentColor" />
              </symbol>
              <symbol id="fc-ts" viewBox="0 0 24 24">
                <path
                  d="M5 22 L5 10 L8 10 L8 7 L12 4 L16 7 L16 10 L19 10 L19 22 Z M9 22 L9 16 L15 16 L15 22 M7 10 L7 8 M11 10 L11 8 M13 10 L13 8 M17 10 L17 8"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </symbol>
              <symbol id="fc-dm" viewBox="0 0 16 16">
                <path d="M8 2 L11 8 L8 14 L5 8 Z" fill="currentColor" />
              </symbol>
            </defs>
          </svg>

          {renderJoinCards("top")}

          {teams.map((team) => {
            const isMyTeam = currentUserTeam?.id === team.id;
            const handleClick = isMyTeam
              ? () => {
                  const routeLocale = params.locale || "en";
                  router.push(
                    `/${routeLocale}/tournaments/${tournament?.slug}/my-team`,
                  );
                }
              : undefined;

            const createdAt = team.createdAt
              ? new Date(team.createdAt)
              : null;
            const joinedLabel = createdAt
              ? differenceInDays(new Date(), createdAt) > 10
                ? formatInTimeZone(
                    createdAt,
                    "Europe/Helsinki",
                    "dd.MM.yyyy HH:mm",
                  )
                : formatDistanceToNow(createdAt, {
                    addSuffix: true,
                    locale: dateLocale,
                  })
              : null;

            const visiblePlayerIds = team.playerIds.slice(0, 3);
            const extraPlayers = team.playerIds.length - visiblePlayerIds.length;
            const hideNames = team.status === TeamStatus.FORFEIT;

            return (
              <div
                key={team.id}
                role={isMyTeam ? "link" : undefined}
                tabIndex={isMyTeam ? 0 : undefined}
                onClick={handleClick}
                onKeyDown={
                  isMyTeam
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleClick?.();
                        }
                      }
                    : undefined
                }
                className={`fc${isMyTeam ? " fc-clickable" : ""}`}
              >
                <svg className="fc-corner tl">
                  <use href="#fc-cn" />
                </svg>
                <svg className="fc-corner tr">
                  <use href="#fc-cn" />
                </svg>
                <svg className="fc-corner bl">
                  <use href="#fc-cn" />
                </svg>
                <svg className="fc-corner br">
                  <use href="#fc-cn" />
                </svg>

                <div className="fc-banner">
                  {team.name || t("common.unnamedTeam")}
                </div>

                <div className="fc-portraits">
                  {visiblePlayerIds.map((playerId, avatarIndex) => {
                    const player = players.get(playerId);
                    const variant = `t${avatarIndex % 3}`;
                    const hiddenLabel = t("teams.forfeit.hiddenPlayer");
                    return (
                      <div
                        key={playerId}
                        className={`fc-portrait ${variant}`}
                        title={hideNames ? hiddenLabel : player?.name}
                      >
                        {!hideNames && player?.photoURL ? (
                          <Image
                            src={player.photoURL}
                            alt={player.name || ""}
                            width={64}
                            height={64}
                          />
                        ) : (
                          getInitials(hideNames ? hiddenLabel : player?.name)
                        )}
                      </div>
                    );
                  })}
                  {extraPlayers > 0 && (
                    <div
                      className={`fc-portrait t${visiblePlayerIds.length % 3}`}
                    >
                      +{extraPlayers}
                    </div>
                  )}
                </div>

                <div className="fc-players">
                  {team.playerIds.map((id) => (
                    <span key={id} className="nm">
                      {hideNames
                        ? t("teams.forfeit.hiddenPlayer")
                        : players.get(id)?.name || t("common.unknownPlayer")}
                    </span>
                  ))}
                </div>

                {joinedLabel && (
                  <div className="fc-joined">
                    {t("tournaments.tower.joinedAt", { date: joinedLabel })}
                  </div>
                )}

                <div className="fc-divider">
                  <svg className="fc-emblem">
                    <use href="#fc-ts" />
                  </svg>
                </div>
              </div>
            );
          })}

          {teams.length > 9 && renderJoinCards("bottom")}
        </div>
      )}
    </div>
  );
}
